import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { useMeta } from "@/lib/hooks";
import { Card, PageHeader, StatusPill, Field } from "@/components/common";
import { fmtDate } from "@/lib/brand";

const FIELDS = {
  external_id: "External ID", title: "Title", category_name: "Category", subcategory_name: "Subcategory", owner_name: "Owner",
  event_date: "Date", next_date: "Next occurrence", countdown: "Countdown", urgency: "Urgency", recurrence: "Recurrence",
  alert_offsets: "Alert offsets", status: "Status", cost_currency: "Currency", cost_amount: "Amount", tags: "Tags", notes: "Notes", attachment_count: "Attachments (count)",
};

function ImportWizard() {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const loadJobs = () => api.get("/import/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  useEffect(() => { loadJobs(); }, []);

  const preview = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/import/preview", fd); setJob(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/import/${job.id}/confirm`, { skip_duplicates: skipDup }); setResult(data); toast.success(`Import ${data.status}`); loadJobs(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => { setJob(null); setFile(null); setResult(null); };

  return (
    <Card title="Excel Import" testid="import-card">
      <ol className="text-sm text-[var(--pm-muted)] list-decimal pl-5 mb-5 space-y-1">
        <li>Download the template (blank or with examples).</li><li>Fill in your reminders and upload the .xlsx file.</li><li>Review the preview, then confirm. Nothing is saved until you confirm.</li>
      </ol>
      <div className="flex flex-wrap gap-2.5 mb-6">
        <button className="pm-btn pm-btn-outline" onClick={() => downloadFile("/excel/template").catch(() => toast.error("Download failed"))} data-testid="template-blank-btn"><Download className="w-4 h-4" /> Blank template</button>
        <button className="pm-btn pm-btn-outline" onClick={() => downloadFile("/excel/template", { examples: true }).catch(() => toast.error("Download failed"))} data-testid="template-examples-btn"><Download className="w-4 h-4" /> Template with examples</button>
      </div>
      {!job ? (
        <div className="flex flex-wrap items-center gap-3">
          <label className="pm-btn pm-btn-soft cursor-pointer" data-testid="import-file-label"><FileSpreadsheet className="w-4 h-4" /> {file ? file.name : "Choose .xlsx file"}
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="import-file-input" /></label>
          <button className="pm-btn pm-btn-primary" disabled={!file || busy} onClick={preview} data-testid="import-preview-btn"><Upload className="w-4 h-4" /> {busy ? "Checking…" : "Upload & preview"}</button>
        </div>
      ) : (
        <div data-testid="import-preview">
          <div className="flex flex-wrap gap-2 mb-4">
            <StatusPill urgency="upcoming">{job.counts.total} rows</StatusPill>
            <StatusPill urgency="completed" testid="import-count-create">{job.counts.create} to create</StatusPill>
            <StatusPill urgency="due_soon" testid="import-count-update">{job.counts.update} to update</StatusPill>
            <StatusPill urgency="overdue" testid="import-count-skip">{job.counts.skip} with errors</StatusPill>
            {job.counts.duplicates > 0 && <StatusPill urgency="archived">{job.counts.duplicates} possible duplicates</StatusPill>}
          </div>
          <div className="overflow-auto max-h-[420px] rounded-xl border border-[var(--pm-border)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F8F6FF]"><tr className="text-left text-xs text-[var(--pm-muted)]"><th className="p-2.5">Row</th><th className="p-2.5">Action</th><th className="p-2.5">Title</th><th className="p-2.5">Category</th><th className="p-2.5">Date</th><th className="p-2.5">Messages</th></tr></thead>
              <tbody>{job.rows.map((r) => (
                <tr key={r.row} className="border-t border-[var(--pm-border)]" data-testid={`import-row-${r.row}`}>
                  <td className="p-2.5">{r.row}</td>
                  <td className="p-2.5"><StatusPill urgency={r.action === "skip" ? "overdue" : r.action === "update" ? "due_soon" : "completed"}>{r.action}</StatusPill></td>
                  <td className="p-2.5 font-semibold">{r.data.title || "—"}</td><td className="p-2.5">{r.data.category_name || "—"}</td><td className="p-2.5">{r.data.event_date ? fmtDate(r.data.event_date) : "—"}</td>
                  <td className="p-2.5 text-xs">{r.errors.map((e) => <p key={e} className="text-[var(--pm-danger)]">{e}</p>)}{r.warnings.map((w) => <p key={w} className="text-[#A86206]">{w}</p>)}</td>
                </tr>))}</tbody>
            </table>
          </div>
          {result ? (
            <div className="mt-5 rounded-xl bg-[#E3F6F1] p-4 text-sm" data-testid="import-result">
              <p className="font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Import {result.status}</p>
              <p>{result.created} created · {result.updated} updated · {result.skipped} skipped{result.failed ? ` · ${result.failed} failed` : ""}</p>
              <div className="flex gap-2 mt-3">
                <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => downloadFile(`/import/${job.id}/errors.xlsx`)} data-testid="import-report-btn">Download row report</button>
                <button className="pm-btn pm-btn-sm pm-btn-primary" onClick={reset} data-testid="import-again-btn">Import another file</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-4 mt-5">
              {job.counts.duplicates > 0 && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} className="accent-[var(--pm-primary)]" data-testid="import-skip-dup-checkbox" /> Skip possible duplicates</label>}
              {job.counts.skip > 0 && <p className="text-xs text-[var(--pm-muted)] flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Rows with errors will be skipped.</p>}
              <button className="pm-btn pm-btn-outline" onClick={reset} data-testid="import-cancel-btn">Cancel</button>
              <button className="pm-btn pm-btn-primary" disabled={busy || job.counts.create + job.counts.update === 0} onClick={confirm} data-testid="import-confirm-btn">{busy ? "Importing…" : `Confirm import (${job.counts.create + job.counts.update})`}</button>
            </div>
          )}
        </div>
      )}
      {jobs.length > 0 && (
        <div className="mt-8"><p className="pm-label">Recent imports</p>
          {jobs.slice(0, 5).map((j) => <div key={j.id} className="flex justify-between text-sm py-2 border-b border-[var(--pm-border)] last:border-0"><span className="truncate">{j.file_name}</span><span className="text-[var(--pm-muted)] text-xs">{j.status} · {fmtDate(j.created_at)}</span></div>)}
        </div>
      )}
    </Card>
  );
}

function ExportPanel() {
  const { meta, entities } = useMeta();
  const [p, setP] = useState({ status: "active", urgency: "", category_id: "", owner_id: "" });
  const [fields, setFields] = useState(Object.keys(FIELDS));
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await downloadFile("/excel/export", { ...p, fields: fields.join(",") }); toast.success("Export downloaded"); } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };
  return (
    <Card title="Excel Export" testid="export-card">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Status"><select className="pm-input" value={p.status} onChange={(e) => setP({ ...p, status: e.target.value })} data-testid="export-status-select"><option value="all">All</option><option value="active">Active</option><option value="completed">Completed</option><option value="archived">Archived</option></select></Field>
        <Field label="Urgency"><select className="pm-input" value={p.urgency} onChange={(e) => setP({ ...p, urgency: e.target.value })} data-testid="export-urgency-select"><option value="">Any</option><option value="overdue">Overdue</option><option value="due_soon,due_today">Due soon</option><option value="upcoming">Upcoming</option></select></Field>
        <Field label="Category"><select className="pm-input" value={p.category_id} onChange={(e) => setP({ ...p, category_id: e.target.value })} data-testid="export-category-select"><option value="">All</option>{meta?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        <Field label="Person / asset"><select className="pm-input" value={p.owner_id} onChange={(e) => setP({ ...p, owner_id: e.target.value })} data-testid="export-owner-select"><option value="">All</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></Field>
      </div>
      <p className="pm-label mt-5">Columns</p>
      <div className="flex flex-wrap gap-2">{Object.entries(FIELDS).map(([k, l]) => <button key={k} className="pm-tab" data-active={fields.includes(k)} onClick={() => setFields(fields.includes(k) ? fields.filter((x) => x !== k) : [...fields, k])} data-testid={`export-field-${k}`}>{l}</button>)}</div>
      <div className="rounded-xl bg-[#FEF3E2] text-[#7A4705] text-xs p-3 mt-5">Exports never include document numbers or file contents. Treat downloaded spreadsheets as private.</div>
      <button className="pm-btn pm-btn-primary mt-5" onClick={go} disabled={busy || !fields.length} data-testid="export-download-btn"><Download className="w-4 h-4" /> {busy ? "Preparing…" : "Download .xlsx"}</button>
    </Card>
  );
}

export default function ImportExport() {
  return (
    <div data-testid="import-export-page">
      <PageHeader title="Import & Export" subtitle="Bring reminders in from Excel with validation, or export a filtered spreadsheet." />
      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-6 items-start"><ImportWizard /><ExportPanel /></div>
    </div>
  );
}
